/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars, unused-imports/no-unused-vars */

import { i18n } from "next-i18next";
import {
  FixedDiscountIcon,
  FreeItemIcon,
  PercentageIcon,
  PercentageIcon2,
} from "../../../components/global/Icons/loyalty.icons";
import {
  CartLoyaltyV2SliceType,
  ItemOfferDetails,
  Localized,
  LocalizedString,
  OfferDetailsBase,
  OfferPayload,
  OfferUIDetails,
} from "../../../services/redux/types/CartLoyaltyV2Types";
import CartItem from "../../CartItem";
import {
  MAIN_OFFER_TYPE,
  OFFER_CATEGORY,
  OrderTypesKeysEnum,
} from "../../enums";
import { getDateWithTime } from "../../help/offers/offers";
import { capitalizeFirstLetter } from "../../help/textPreparation/textPreparation";
import {
  BogoOfferDetailsType,
  BundleOfferDetailsType,
  DiscountedItemOfferDetailsType,
  FixedAmountOfferDetailsType,
  FreeItemOfferDetailsType,
  MultiplierOfferDetailsType,
  OFFER_SYSTEM,
  OfferType,
  PackageOfferExtras,
  PercentageAmountOfferDetailsType,
} from "../../types";
import { IsOfferApplicableArgs } from "../types";

export default abstract class Offer {
  id: number;
  offerSystem: OFFER_SYSTEM;
  expiryDate?: Date;
  orderTypes: OrderTypesKeysEnum[];
  remainingToEligiableOfferValue?: number;
  milestonePoints?: number;
  extras?: PackageOfferExtras;
  abstract offerType: MAIN_OFFER_TYPE | string;
  abstract details:
    | MultiplierOfferDetailsType
    | PercentageAmountOfferDetailsType
    | FixedAmountOfferDetailsType
    | FreeItemOfferDetailsType
    | DiscountedItemOfferDetailsType
    | BundleOfferDetailsType
    | BogoOfferDetailsType
    | OfferDetailsBase;

  offerCategory: OFFER_CATEGORY;
  uiDetails?: OfferUIDetails;
  isCartV2?: boolean;
  isSubOffer?: boolean;

  retrieveI18ValueByKey: (key: string) => string;

  protected constructor(offer: OfferType) {
    this.id = offer.id;
    this.offerSystem = offer.offerSystem;
    this.expiryDate = offer.expiryDate;
    this.orderTypes = offer.orderTypes;
    this.remainingToEligiableOfferValue = offer.remainingToEligiableOfferValue;
    this.milestonePoints = offer.milestonePoints;
    this.extras = offer.extras;
    this.retrieveI18ValueByKey = i18n?.t ?? (() => "");
    this.uiDetails = offer.uiDetails;
    this.offerCategory = offer.offerCategory;
    this.isSubOffer = offer.isSubOffer;
  }

  static get lng() {
    return (i18n?.language ?? "en") as keyof LocalizedString;
  }

  static getLocalizedValue<T>(localized?: Localized<T>): T | undefined {
    if (!localized) {
      return undefined;
    }

    const fullLang = (i18n?.language ?? "en") as keyof Localized<T>;
    const baseLang = (fullLang as string).split("-")[0] as keyof Localized<T>;
    const keys = Array.from(
      new Set([fullLang, baseLang, "en"])
    ) as (keyof Localized<T>)[];

    for (const key of keys) {
      const value = localized[key];

      if (value) {
        return value;
      }
    }

    return undefined;
  }

  applyOffer(
    cartV2: CartLoyaltyV2SliceType,
    cartItem?: CartItem[],
    subOffer?: OfferPayload,
    extraDetails?: { isNotFirstApply?: boolean }
  ): OfferPayload | null {
    return {
      id: this.id,
      offerType: this.offerType,
      offerCategory: this.offerCategory,
      details: this.details,
      uiDetails: this.uiDetails,
    };
  }

  numberOfSteps(): number {
    return 0;
  }

  isPercentageOffer() {
    return false;
  }

  isFreeItemOffer() {
    return false;
  }

  isDiscountItemOffer() {
    return false;
  }

  isMultiplierOffer() {
    return false;
  }

  isItemFromListOffer() {
    return false;
  }

  isItemOffer() {
    return false;
  }

  isBundleOffer() {
    return false;
  }

  isBogoOffer() {
    return false;
  }

  isTimedOffer() {
    return (
      "from" in this?.details &&
      "to" in this?.details &&
      this?.details?.from &&
      this?.details?.to
    );
  }

  isTimedApplicable(timeZone: string) {
    if (
      "from" in this?.details &&
      "to" in this?.details &&
      this?.details?.from &&
      this?.details?.to
    ) {
      const now = new Date();

      const franchiseTime = now.toLocaleString("en-US", {
        timeZone,
      });

      const nowWithFranchiseTimezone = new Date(franchiseTime);

      const offerStartDate = getDateWithTime(
        nowWithFranchiseTimezone,
        this.details.from
      );
      const offerEndDate = getDateWithTime(
        nowWithFranchiseTimezone,
        this.details.to
      );

      return (
        nowWithFranchiseTimezone >= offerStartDate &&
        nowWithFranchiseTimezone <= offerEndDate
      );
    }

    return true;
  }

  abstract isOfferApplicableByType(args: IsOfferApplicableArgs): boolean;

  isOfferApplicable(args: IsOfferApplicableArgs): boolean {
    const now = new Date();

    if (this.expiryDate) {
      const expiryDate = new Date(this.expiryDate);

      if (now > expiryDate) {
        return false;
      }
    }

    return (
      this.isOfferApplicableByType(args) &&
      this.isTimedApplicable(args.timeZone)
    );
  }

  hasDeletedItems(): boolean {
    return false;
  }

  containsDisabledItems(disabledItemsIds: number[]): boolean {
    if (this.offerCategory === OFFER_CATEGORY.ITEM_OFFER) {
      return (this.details as ItemOfferDetails).subItems.some(
        (si) => si.itemId && disabledItemsIds.includes(si.itemId)
      );
    }

    return false;
  }

  isOfferApplicableOnExcludedItems() {
    return true;
  }

  isEqual(offer: Offer | OfferPayload | undefined) {
    if (!offer) {
      return false;
    }
    if (offer instanceof Offer) {
      return this.compareOffer(offer);
    } else {
      return this.compareRedeemedOffer(offer);
    }
  }

  protected compareOffer(offer: Offer) {
    return this.id === offer.id;
  }

  protected compareRedeemedOffer(offer: OfferPayload) {
    return this.id === offer.id;
  }

  // -- TEXT FUNCTIONS SECTION - START --

  getWelcomePopupOfferText(): string {
    return Offer.getLocalizedValue(this.uiDetails?.mainText) ?? "";
  }

  getVerifyMailPopupOfferText(
    _currency: string,
    _language: string = "en"
  ): string {
    return Offer.getLocalizedValue(this.uiDetails?.mainText) || "";
  }

  getOrganizationCustomerOfferText(_currency: string): string {
    const appliedI18nText = this.retrieveI18ValueByKey("common:offer-applied");

    return `${capitalizeFirstLetter(
      Offer.getLocalizedValue(this.uiDetails?.mainText) ?? ""
    )} ${appliedI18nText}`;
  }

  getOfferCartText(): JSX.Element {
    return <>{Offer.getLocalizedValue(this.uiDetails?.mainText)}</>;
  }

  shouldHideOffer(disabledItemsIds: number[]): boolean {
    return false;
  }

  prepareOfferPromocodeText(): string {
    return `${Offer.getLocalizedValue(this.uiDetails?.mainText) ?? ""}`;
  }

  offerCardDetailsText(props: { short: boolean; index?: number }) {
    const offerCard = Array.isArray(this.uiDetails?.offerCard)
      ? this.uiDetails?.offerCard[props.index ?? 0]
      : this.uiDetails?.offerCard;
    const details = props.short
      ? offerCard?.shortDetailsText
      : offerCard?.detailsText;

    return details ? Offer.getLocalizedValue(details) : undefined;
  }

  // -- TEXT FUNCTIONS SECTION - END --

  static mapOfferIcon(
    iconValue: string,
    alternatePercentageIcon: boolean | undefined
  ) {
    switch (iconValue) {
      case "percentage":
        return alternatePercentageIcon ? (
          <PercentageIcon2 />
        ) : (
          <PercentageIcon />
        );
      case "fixed":
      case "dollar":
        return <FixedDiscountIcon />;
      case "free":
      case "gift":
        return <FreeItemIcon />;
      default:
        return <></>;
    }
  }

  twistOfferIsAvailable(isTwistOfferAvailable?: boolean): boolean {
    return Boolean(
      isTwistOfferAvailable &&
        this.offerSystem !== OFFER_SYSTEM.POINTING_OFFER &&
        this.offerSystem !== OFFER_SYSTEM.ORGANIZATION_OFFER
    );
  }
}
